// @ts-nocheck
/**
 * Carbon Form Validation Utilities
 * Provides comprehensive validation patterns and utilities for Carbon forms
 * Implements WCAG 2.1 AA compliance and Carbon Design System patterns
 */

import React, { useState, useEffect, useCallback } from 'react';
import { InlineNotification, ToastNotification, FormLabel, HelperText } from '@carbon/react';
import { CheckmarkFilled, WarningFilled, ErrorFilled, InformationFilled } from '@carbon/icons-react';

// Validation rule types
export interface ValidationRule {
  type: 'required' | 'email' | 'min' | 'max' | 'pattern' | 'custom' | 'match';
  value?: any;
  message: string;
  validator?: (value: any, formData?: any) => boolean | Promise<boolean>;
}

export interface FieldValidation {
  field: string;
  rules: ValidationRule[];
  dependencies?: string[];
}

export interface ValidationResult {
  valid: boolean;
  errors: Record<string, string>;
  warnings?: Record<string, string>;
}

export interface FormValidationState {
  errors: Record<string, string>;
  warnings: Record<string, string>;
  touched: Record<string, boolean>;
  validating: Record<string, boolean>;
}

/**
 * Core validation functions
 */
export const validators = {
  required: (value: any): boolean => {
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'string') return value.trim().length > 0;
    if (typeof value === 'boolean') return value === true;
    return value !== null && value !== undefined && value !== '';
  },

  email: (value: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  },

  min: (value: string | number | any[], minValue: number): boolean => {
    if (typeof value === 'string' || Array.isArray(value)) {
      return value.length >= minValue;
    }
    return Number(value) >= minValue;
  },

  max: (value: string | number | any[], maxValue: number): boolean => {
    if (typeof value === 'string' || Array.isArray(value)) {
      return value.length <= maxValue;
    }
    return Number(value) <= maxValue;
  },

  pattern: (value: string, pattern: RegExp): boolean => {
    return pattern.test(value);
  },

  match: (value: any, matchValue: any): boolean => {
    return value === matchValue;
  },

  // Common validation patterns
  password: (value: string): boolean => {
    // At least 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return passwordRegex.test(value);
  },

  username: (value: string): boolean => {
    // Alphanumeric, underscore, hyphen, 3-20 chars
    const usernameRegex = /^[a-zA-Z0-9_-]{3,20}$/;
    return usernameRegex.test(value);
  },

  phone: (value: string): boolean => {
    // Basic phone validation (10-15 digits)
    const phoneRegex = /^\+?[\d\s-()]+$/;
    return phoneRegex.test(value) && value.replace(/\D/g, '').length >= 10;
  },

  url: (value: string): boolean => {
    try {
      new URL(value);
      return true;
    } catch {
      return false;
    }
  },

  date: (value: string | Date): boolean => {
    const date = value instanceof Date ? value : new Date(value);
    return !isNaN(date.getTime());
  },

  age: (value: Date | string, minAge: number, maxAge?: number): boolean => {
    const birthDate = value instanceof Date ? value : new Date(value);
    const today = new Date();
    const age = Math.floor((today.getTime() - birthDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000));

    if (maxAge !== undefined) {
      return age >= minAge && age <= maxAge;
    }
    return age >= minAge;
  },
};

/**
 * Validate a single field
 */
export const validateField = async (value: any, rules: ValidationRule[], formData?: any): Promise<string | null> => {
  for (const rule of rules) {
    let isValid = false;

    switch (rule.type) {
      case 'required':
        isValid = validators.required(value);
        break;
      case 'email':
        isValid = validators.email(value);
        break;
      case 'min':
        isValid = validators.min(value, rule.value);
        break;
      case 'max':
        isValid = validators.max(value, rule.value);
        break;
      case 'pattern':
        isValid = validators.pattern(value, rule.value);
        break;
      case 'match':
        isValid = validators.match(value, rule.value);
        break;
      case 'custom':
        if (rule.validator) {
          isValid = await rule.validator(value, formData);
        }
        break;
    }

    if (!isValid) {
      return rule.message;
    }
  }

  return null;
};

/**
 * Validate entire form
 */
export const validateForm = async (
  formData: Record<string, any>,
  validations: FieldValidation[]
): Promise<ValidationResult> => {
  const errors: Record<string, string> = {};
  const warnings: Record<string, string> = {};

  for (const validation of validations) {
    const fieldValue = formData[validation.field];
    const error = await validateField(fieldValue, validation.rules, formData);

    if (error) {
      errors[validation.field] = error;
    }
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
    warnings,
  };
};

/**
 * Custom hook for form validation
 */
export const useFormValidation = (initialValues: Record<string, any>, validations: FieldValidation[]) => {
  const [values, setValues] = useState(initialValues);
  const [state, setState] = useState<FormValidationState>({
    errors: {},
    warnings: {},
    touched: {},
    validating: {},
  });

  // Validate field on change
  const validateFieldOnChange = useCallback(
    async (field: string, value: any) => {
      const validation = validations.find(v => v.field === field);
      if (!validation) return;

      setState(prev => ({
        ...prev,
        validating: { ...prev.validating, [field]: true },
      }));

      const error = await validateField(value, validation.rules, values);

      setState(prev => ({
        ...prev,
        errors: {
          ...prev.errors,
          [field]: error || '',
        },
        validating: {
          ...prev.validating,
          [field]: false,
        },
      }));
    },
    [validations, values]
  );

  // Handle field change
  const handleChange = useCallback(
    (field: string, value: any) => {
      setValues(prev => ({
        ...prev,
        [field]: value,
      }));

      // Mark field as touched
      setState(prev => ({
        ...prev,
        touched: {
          ...prev.touched,
          [field]: true,
        },
      }));

      // Validate on change if touched
      if (state.touched[field]) {
        validateFieldOnChange(field, value);
      }
    },
    [state.touched, validateFieldOnChange]
  );

  // Handle field blur
  const handleBlur = useCallback(
    (field: string) => {
      setState(prev => ({
        ...prev,
        touched: {
          ...prev.touched,
          [field]: true,
        },
      }));

      validateFieldOnChange(field, values[field]);
    },
    [values, validateFieldOnChange]
  );

  // Validate entire form
  const validate = useCallback(async (): Promise<boolean> => {
    const result = await validateForm(values, validations);

    setState(prev => ({
      ...prev,
      errors: result.errors,
      warnings: result.warnings || {},
      touched: Object.keys(values).reduce(
        (acc, key) => ({
          ...acc,
          [key]: true,
        }),
        {}
      ),
    }));

    return result.valid;
  }, [values, validations]);

  // Reset form
  const reset = useCallback(() => {
    setValues(initialValues);
    setState({
      errors: {},
      warnings: {},
      touched: {},
      validating: {},
    });
  }, [initialValues]);

  return {
    values,
    errors: state.errors,
    warnings: state.warnings,
    touched: state.touched,
    validating: state.validating,
    isValid: Object.keys(state.errors).length === 0,
    handleChange,
    handleBlur,
    validate,
    reset,
    setValues,
  };
};

/**
 * Carbon Form Error Display Component
 */
export const CarbonFormError: React.FC<{
  error?: string;
  warning?: string;
  touched?: boolean;
  id: string;
}> = ({ error, warning, touched, id }) => {
  if (!touched) return null;

  if (error) {
    return (
      <div className="bx--form-requirement" id={`${id}-error`} role="alert" aria-live="polite">
        <ErrorFilled size={16} aria-hidden="true" />
        <span>{error}</span>
      </div>
    );
  }

  if (warning) {
    return (
      <div
        className="bx--form-requirement bx--form-requirement--warning"
        id={`${id}-warning`}
        role="status"
        aria-live="polite"
      >
        <WarningFilled size={16} aria-hidden="true" />
        <span>{warning}</span>
      </div>
    );
  }

  return null;
};

/**
 * Carbon Form Success Indicator
 */
export const CarbonFormSuccess: React.FC<{
  show: boolean;
  message?: string;
}> = ({ show, message = 'Valid' }) => {
  if (!show) return null;

  return (
    <div className="bx--form-requirement bx--form-requirement--success" role="status" aria-live="polite">
      <CheckmarkFilled size={16} aria-hidden="true" />
      <span>{message}</span>
    </div>
  );
};

/**
 * Carbon Form Notification Component
 */
export const CarbonFormNotification: React.FC<{
  type: 'error' | 'warning' | 'info' | 'success';
  title: string;
  subtitle?: string;
  hideCloseButton?: boolean;
  onClose?: () => void;
}> = ({ type, title, subtitle, hideCloseButton = false, onClose }) => {
  return (
    <InlineNotification
      kind={type}
      title={title}
      subtitle={subtitle}
      hideCloseButton={hideCloseButton}
      onClose={onClose}
      aria-live={type === 'error' ? 'assertive' : 'polite'}
    />
  );
};

/**
 * Password strength validator and indicator
 */
export const getPasswordStrength = (
  password: string
): {
  strength: 0 | 1 | 2 | 3 | 4;
  label: string;
  color: string;
} => {
  let strength = 0;

  if (password.length >= 8) strength++;
  if (password.length >= 12) strength++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
  if (/\d/.test(password)) strength++;
  if (/[@$!%*?&#^()]/.test(password)) strength++;

  const strengthLevels = [
    { strength: 0, label: 'Very Weak', color: '#da1e28' },
    { strength: 1, label: 'Weak', color: '#ff832b' },
    { strength: 2, label: 'Fair', color: '#f1c21b' },
    { strength: 3, label: 'Good', color: '#42be65' },
    { strength: 4, label: 'Strong', color: '#24a148' },
  ];

  const level = Math.min(strength, 4);
  return {
    strength: level as 0 | 1 | 2 | 3 | 4,
    ...strengthLevels[level],
  };
};

/**
 * Common validation patterns for reuse
 */
export const commonValidations = {
  email: [
    { type: 'required' as const, message: 'Email is required' },
    { type: 'email' as const, message: 'Please enter a valid email address' },
  ],

  password: [
    { type: 'required' as const, message: 'Password is required' },
    { type: 'min' as const, value: 8, message: 'Password must be at least 8 characters' },
    {
      type: 'pattern' as const,
      value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/,
      message: 'Password must contain uppercase, lowercase, number and special character',
    },
  ],

  username: [
    { type: 'required' as const, message: 'Username is required' },
    { type: 'min' as const, value: 3, message: 'Username must be at least 3 characters' },
    { type: 'max' as const, value: 20, message: 'Username must not exceed 20 characters' },
    {
      type: 'pattern' as const,
      value: /^[a-zA-Z0-9_-]+$/,
      message: 'Username can only contain letters, numbers, underscore and hyphen',
    },
  ],

  phone: [
    { type: 'required' as const, message: 'Phone number is required' },
    {
      type: 'pattern' as const,
      value: /^\+?[\d\s-()]+$/,
      message: 'Please enter a valid phone number',
    },
  ],

  url: [
    { type: 'required' as const, message: 'URL is required' },
    {
      type: 'custom' as const,
      message: 'Please enter a valid URL',
      validator: (value: string) => validators.url(value),
    },
  ],
};

export default {
  validators,
  validateField,
  validateForm,
  useFormValidation,
  CarbonFormError,
  CarbonFormSuccess,
  CarbonFormNotification,
  getPasswordStrength,
  commonValidations,
};
